import { Pipe, PipeTransform } from '@angular/core';
import { IDataPullSectionMapping, IDataPullSectionMappingField } from '../interfaces/pull-data-group.interface';

@Pipe({
  name: 'pullDataGroupTitle'
})
export class PullDataGroupTitlePipe implements PipeTransform {
  private static cache = new Map<string, string>();
  private static readonly CACHE_SIZE = 1000;

  static clearCache(): void {
    PullDataGroupTitlePipe.cache.clear();
  }

  transform(group: IDataPullSectionMapping): string {
    if (!group || !group.values) {
      return '';
    }

    const cacheKey = this.createCacheKey(group);

    if (PullDataGroupTitlePipe.cache.has(cacheKey)) {
      const cachedValue = PullDataGroupTitlePipe.cache.get(cacheKey);
      return cachedValue ?? '';
    }

    const title = this.calculateTitle(group);

    this.setCache(cacheKey, title);

    return title;
  }

  private createCacheKey(group: IDataPullSectionMapping): string {
    const values = group.values.map(v => `${v.vendorPath}:${v.value}`).join('|');
    const sectionLabel = group.values[0]?.sectionLabel || '';
    return `${sectionLabel}-${values}`;
  }

  private calculateTitle(group: IDataPullSectionMapping): string {
    let title = '';
    const firstName = '';
    const lastName = '';
    const dateOfBirth = '';
    const gender = '';
    const make = '';
    const model = '';
    const year = '';
    const address = '';

    for (const value of group.values) {
      this.fillVariable(value, {
        firstName,
        lastName,
        dateOfBirth,
        gender,
        make,
        model,
        year,
        address
      });
    }

    title = this.buildTitle({
      firstName,
      lastName,
      dateOfBirth,
      gender,
      make,
      model,
      year,
      address
    });

    if (!title.trim() && group.values[0]?.sectionLabel) {
      title = group.values[0].sectionLabel;
    }

    return title;
  }

  private fillVariable(value: IDataPullSectionMappingField, variables: any): void {
    switch (value.vendorPath) {
      case 'drivers.firstName':
        variables.firstName = value.value as string;
        break;
      case 'drivers.lastName':
        variables.lastName = value.value as string;
        break;
      case 'drivers.dateOfBirth':
        variables.dateOfBirth = value.value as string;
        break;
      case 'drivers.gender':
        variables.gender = value.value as string;
        break;
      case 'vehicles.make':
        variables.make = value.value as string;
        break;
      case 'vehicles.model':
        variables.model = value.value as string;
        break;
      case 'vehicles.year':
        variables.year = value.value as string;
        break;
      default:
        if (value.vendorPath?.includes('address')) {
          variables.address = value.value as string;
        }
        break;
    }
  }

  private buildTitle(variables: any): string {
    const parts: string[] = [];

    if (variables.firstName || variables.lastName) {
      const personParts = [variables.firstName, variables.lastName].filter(Boolean);
      if (personParts.length > 0) {
        parts.push(personParts.join(' '));
      }
    }

    if (variables.gender) {
      parts.push(variables.gender);
    }
    if (variables.dateOfBirth) {
      parts.push(variables.dateOfBirth);
    }

    if (variables.year || variables.make || variables.model) {
      const vehicleParts = [variables.year, variables.make, variables.model].filter(Boolean);
      if (vehicleParts.length > 0) {
        parts.push(vehicleParts.join(' '));
      }
    }

    if (parts.length === 0 && variables.address) {
      parts.push(variables.address);
    }

    return parts.join(' | ');
  }

  private setCache(key: string, value: string): void {
    if (PullDataGroupTitlePipe.cache.size >= PullDataGroupTitlePipe.CACHE_SIZE) {
      const firstKey = PullDataGroupTitlePipe.cache.keys().next().value;
      PullDataGroupTitlePipe.cache.delete(firstKey);
    }

    PullDataGroupTitlePipe.cache.set(key, value);
  }
}
